import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

function AnimatedSection({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setVisible(true);
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
    >
      {children}
    </div>
  );
}

const programs = [
  {
    title: "Bantuan Makanan",
    subtitle: "Program Jom Berbuka & Bekalan Harian",
    desc: "Kami menyediakan bekalan makanan harian kepada keluarga yang memerlukan, terutamanya semasa musim banjir dan krisis. Program Jom Berbuka menyediakan juadah berbuka puasa percuma sepanjang Ramadan kepada golongan miskin, gelandangan, dan pelarian.",
    image:
      "https://readdy.ai/api/search-image?query=Malaysian%20volunteers%20preparing%20and%20packing%20food%20boxes%20in%20a%20community%20kitchen%20warm%20lighting%20halal%20food%20packaging%20earth%20tone%20colors%20green%20aprons%20organized%20stacks%20humanitarian%20aid%20photography&width=700&height=450&seq=progfood&orientation=landscape",
    icon: "ri-restaurant-line",
    iconColor: "text-primary-600",
    bgColor: "bg-primary-50",
    stats: [
      { label: "Kotak Makanan", value: "15,000+" },
      { label: "Keluarga Dibantu", value: "2,500+" },
    ],
  },
  {
    title: "Pendidikan",
    subtitle: "Kelas Tambahan & Bantuan Sekolah",
    desc: "Kelas tambahan percuma untuk kanak-kanak miskin di subjek Matematik, Bahasa Inggeris, dan Sains. Kami juga menyediakan bantuan kelengkapan sekolah, buku teks, dan yuran pendidikan kepada pelajar yang layak.",
    image:
      "https://readdy.ai/api/search-image?query=Malaysian%20children%20of%20diverse%20ethnicities%20sitting%20in%20a%20bright%20community%20classroom%20learning%20with%20a%20volunteer%20teacher%20warm%20natural%20lighting%20earth%20tone%20decor%20books%20and%20laptops%20hopeful%20atmosphere&width=700&height=450&seq=progedu&orientation=landscape",
    icon: "ri-book-open-line",
    iconColor: "text-warm-600",
    bgColor: "bg-warm-50",
    stats: [
      { label: "Pelajar Dibantu", value: "1,200+" },
      { label: "Kelas Dijalankan", value: "300+" },
    ],
  },
  {
    title: "Perlindungan",
    subtitle: "Perlindungan & Sokongan Mangsa",
    desc: "Memberi perlindungan, kaunseling, dan sokongan sosial kepada mangsa keganasan domestik, penyalahgunaan, dan pelarian. Kami bekerjasama dengan pihak berkuasa dan NGO lain untuk memastikan keselamatan mangsa.",
    image:
      "https://readdy.ai/api/search-image?query=warm%20supportive%20scene%20of%20a%20Malaysian%20female%20counselor%20talking%20with%20a%20young%20woman%20in%20a%20safe%20space%20soft%20warm%20lighting%20comfortable%20setting%20earth%20tone%20palette%20compassionate%20atmosphere&width=700&height=450&seq=progprotect&orientation=landscape",
    icon: "ri-shield-check-line",
    iconColor: "text-teal-600",
    bgColor: "bg-teal-50",
    stats: [
      { label: "Individu Dilindungi", value: "800+" },
      { label: "Sesi Kaunseling", value: "1,500+" },
    ],
  },
  {
    title: "Kesihatan",
    subtitle: "Kempen Kesihatan & Pemeriksaan Percuma",
    desc: "Menganjurkan kempen kesihatan di kawasan miskin bandar, pemeriksaan kesihatan percuma, dan bantuan ubat-ubatan kepada golongan yang tidak mampu. Kami juga memberi kesedaran tentang pemakanan dan gaya hidup sihat.",
    image:
      "https://readdy.ai/api/search-image?query=Malaysian%20medical%20volunteers%20conducting%20a%20free%20health%20checkup%20in%20a%20community%20center%20measuring%20blood%20pressure%20warm%20lighting%20organized%20setup%20earth%20tone%20palette%20caring%20atmosphere&width=700&height=450&seq=proghealth&orientation=landscape",
    icon: "ri-heart-pulse-line",
    iconColor: "text-red-500",
    bgColor: "bg-red-50",
    stats: [
      { label: "Pemeriksaan", value: "3,500+" },
      { label: "Kempen Dijalankan", value: "80+" },
    ],
  },
];

export default function Programs() {
  return (
    <div className="min-h-screen pt-20 md:pt-24">
      {/* Hero */}
      <section className="relative h-[280px] md:h-[350px] flex items-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=aerial%20view%20of%20a%20colorful%20Malaysian%20community%20event%20with%20tents%20and%20people%20gathering%20for%20charity%20warm%20afternoon%20lighting%20vibrant%20but%20earth%20tone%20palette%20green%20and%20warm%20colors%20community%20spirit&width=1600&height=500&seq=proghero&orientation=landscape"
          alt="Program Mulia Insan"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-teal-900/70" />
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <h1 className="font-display text-3xl md:text-5xl font-bold text-white">
            Program Kami
          </h1>
          <p className="text-white/80 text-sm md:text-base mt-3 max-w-xl">
            Empat tonggak utama yang menjadi teras kepada setiap usaha kami
            membantu komuniti yang memerlukan
          </p>
        </div>
      </section>

      {/* Programs list */}
      <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto space-y-14 md:space-y-20">
          {programs.map((prog, idx) => (
            <AnimatedSection key={prog.title}>
              <div
                className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center ${
                  idx % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
              >
                <div className={idx % 2 === 1 ? "lg:order-2" : "lg:order-1"}>
                  <img
                    src={prog.image}
                    alt={prog.title}
                    className="w-full rounded-xl object-cover h-56 md:h-72"
                  />
                </div>
                <div className={idx % 2 === 1 ? "lg:order-1" : "lg:order-2"}>
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className={`w-10 h-10 flex items-center justify-center rounded-full ${prog.bgColor}`}
                    >
                      <i className={`${prog.icon} ${prog.iconColor} text-lg`} />
                    </div>
                    <p className="text-xs font-medium text-teal-600 uppercase tracking-wider">
                      {prog.subtitle}
                    </p>
                  </div>
                  <h2 className="font-display text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                    {prog.title}
                  </h2>
                  <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-6">
                    {prog.desc}
                  </p>
                  <div className="flex flex-wrap gap-6">
                    {prog.stats.map((stat) => (
                      <div key={stat.label}>
                        <div className="text-xl md:text-2xl font-bold text-teal-700">
                          {stat.value}
                        </div>
                        <div className="text-xs text-gray-500">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12 bg-earth-50">
        <AnimatedSection className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-2xl md:text-4xl font-bold text-gray-900 mb-3">
            Sertai Kami Dalam Usaha Mulia
          </h2>
          <p className="text-sm md:text-base text-gray-500 max-w-lg mx-auto mb-8">
            Sumbangan anda membolehkan kami meneruskan program-program ini. Setiap
            ringgit membawa perubahan.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/donate"
              className="inline-flex items-center gap-2 px-6 py-3 bg-teal-700 text-white text-sm font-semibold rounded-full hover:bg-teal-800 transition-colors"
            >
              Sumbang Sekarang <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 text-sm font-medium rounded-full hover:bg-white transition-colors"
            >
              Jadi Sukarelawan
            </Link>
          </div>
        </AnimatedSection>
      </section>
    </div>
  );
}